import React, { useState, useEffect } from 'react';
import { db, auth } from '../utils/firebase';
import logger from '../utils/logger';
import ActivityLog from '../components/ActivityLog';
import WeightTracking from '../components/WeightTracking';
import MealLog from '../components/MealLog';
import ProfileCard from '../components/ProfileCard';
import EnergyBalance from '../components/EnergyBalance';
import BottomTabBar from '../components/BottomTabBar';
import { calculateBMR } from '../utils/bodyMetrics';
import { useToast } from '../context/ToastContext';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import '../styles/user.css';
import '../styles/data-table.css';
import '../styles/buttons.css';
import '../styles/popups.css';
import '../styles/widgets.css';
import '../styles/energy-balance.css';
import '../styles/meal-log.css';


const EMPTY_PROFILE = {
  currentWeight: '',
  targetWeight: '',
  targetDate: '',
  height: '',
  dateOfBirth: '',
  gender: 'Male'
};

function User() {
  const location = useLocation();
  const navigate = useNavigate();
  const showToast = useToast();
  const [userId, setUserId] = useState(null);
  const [profile, setProfile] = useState(EMPTY_PROFILE);
  const [error, setError] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [goal, setGoal] = useState({ target: '', type: 'reps' });
  const [caloriesConsumed, setCaloriesConsumed] = useState(0);
  const [caloriesBurned, setCaloriesBurned] = useState(0);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [helpUnread, setHelpUnread] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserId(user.uid);
        if (!user.emailVerified) {
          navigate('/verify-email');
        }
      } else {
        navigate('/login');
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (!userId) return;
    setProfile(EMPTY_PROFILE);
  }, [userId]);

  useEffect(() => {
    if (!userId) return;

    const fetchProfile = async () => {
      setLoadingProfile(true);
      try {
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setProfile({
            currentWeight: userData.currentWeight?.toString() || '',
            targetWeight: userData.targetWeight?.toString() || '',
            targetDate: userData.targetDate || '',
            height: userData.height?.toString() || '',
            dateOfBirth: userData.dateOfBirth || '',
            gender: userData.gender || 'Male',
            bodyFatPercentage: userData.bodyFatPercentage || null
          });
          // Help version — if the user hasn't seen the current Help content,
          // show the unread dot on the Account tab / nav link. HELP_VERSION
          // is hardcoded here (kept in sync with account.js manually).
          const lastHelpVersion = userData.lastHelpVersion || 0;
          if (lastHelpVersion < 2) setHelpUnread(true);
        } else {
          setProfile(EMPTY_PROFILE);
        }
        const goalDoc = await getDoc(doc(db, 'users', userId, 'preferences', 'goals'));
        if (goalDoc.exists()) {
          const goalData = goalDoc.data();
          setGoal({ target: goalData.target || '', type: goalData.type || 'reps' });
        } else {
          setGoal({ target: '', type: 'reps' });
        }
      } catch (error) {
        logger.error(error, { page: 'User', action: 'fetchProfile' });
        setError('Failed to fetch profile or goal. Please try again.');
      } finally {
        setLoadingProfile(false);
      }
    };

    fetchProfile();
  }, [userId]);

  useEffect(() => {
    if (error) {
      showToast(error, 'error');
      setError(null);
    }
  }, [error, showToast]);

  const handleLogout = () => {
    signOut(auth)
      .then(() => navigate('/login'))
      .catch((err) => {
        logger.error(err, { page: 'User', action: 'logout' });
        setError('Failed to log out. Please try again.');
      });
  };

  return (
    <div className="App">
      <nav className="nav-desktop">
        <Link to="/user" className="nav-brand">FriendsFitness</Link>
        <div className="nav-links-right">
          <Link to="/user" className={`nav-pill${location.pathname === '/user' ? ' active-link' : ''}`}>User</Link>
          <Link to="/stats" className={`nav-pill${location.pathname === '/stats' ? ' active-link' : ''}`}>Stats</Link>
          <Link to="/community" className={`nav-pill${location.pathname === '/community' ? ' active-link' : ''}`}>Community</Link>
          <span style={{ position: 'relative', display: 'inline-block' }}>
            <Link to="/account" className={`nav-pill${location.pathname === '/account' ? ' active-link' : ''}`}>Account</Link>
            {helpUnread && <span aria-hidden="true" style={{ position: 'absolute', top: '4px', right: '4px', width: '8px', height: '8px', borderRadius: '50%', background: '#f97316' }} />}
          </span>
          <button className="logout-button" onClick={handleLogout}>Logout</button>
        </div>
      </nav>
      <BottomTabBar helpUnread={helpUnread} />
      <ActivityLog
        userId={userId}
        currentWeight={profile.currentWeight}
        goal={goal}
        caloriesConsumed={caloriesConsumed}
        onCaloriesBurned={setCaloriesBurned}
        onActivityLogged={() => setRefreshTrigger(n => n + 1)}
        onGoalUpdate={(updatedGoal) => setGoal(updatedGoal)}
        onError={setError}
      />
      <MealLog
        userId={userId}
        onCaloriesChange={setCaloriesConsumed}
        onMealLogged={() => setRefreshTrigger(n => n + 1)}
        onError={setError}
      />
      <EnergyBalance
        bmr={calculateBMR(profile)}
        caloriesBurned={caloriesBurned}
        caloriesConsumed={caloriesConsumed}
        userId={userId}
        currentWeight={profile.currentWeight}
        refreshTrigger={refreshTrigger}
      />
      <ProfileCard
        userId={userId}
        profile={profile}
        setProfile={setProfile}
        loadingProfile={loadingProfile}
        onError={setError}
      />
      <WeightTracking
        userId={userId}
        profileHeight={profile.height}
        onCurrentWeightChange={(newWeight) => setProfile(prev => ({ ...prev, currentWeight: newWeight }))}
        onError={setError}
      />
    </div>
  );
}

export default User;
